import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Globe } from '../components/ui/globe';
import { Link } from 'react-router-dom';
import { ChevronDown, CheckCircle2 } from 'lucide-react';
import { T } from '@tolgee/react';
import { OptimizedImage } from '../components/ui/OptimizedImage';

const CountUp = ({ to, duration = 2 }: { to: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const nodeRef = useRef(null);
  const inView = useInView(nodeRef, { once: true, margin: "-100px" });

  useEffect(() => {
    if (inView) {
      let startTime: number;
      let animationFrame: number;

      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);

        setCount(Math.floor(progress * to));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [inView, to, duration]);

  return <span ref={nodeRef}>{count}</span>;
};

const Nosotros = () => {
  useEffect(() => {
    document.title = "Sobre Nosotros | Jarad - Nutrición Premium para Mascotas";
    window.scrollTo(0, 0);
  }, []);

  const targetRef = useRef(null);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end end"]
  });

  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const heroY = useTransform(heroScroll, [0, 1], ["0%", "50%"]);

  return (
    <>
      <Helmet>
        <title>Sobre Nosotros | Jarad - Nutrición Premium</title>
        <meta name="description" content="Descubre la historia de Jarad Pet Food, nuestros valores y compromiso con la nutrición animal de calidad." />
      </Helmet>

      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-2 bg-[#f6cf02] origin-left z-50"
        style={{ scaleX: scrollYProgress }}
      />

      <div className="bg-[#ffffff]" ref={targetRef}>
        {/* Section 1: Hero */}
        <div ref={heroRef} className="relative h-[95vh] w-full overflow-hidden flex items-center justify-center bg-black">
          <motion.div
            className="absolute inset-0 z-0"
            style={{ y: heroY }}
          >
            <OptimizedImage
              src="/about/hero-bg.webp"
              alt="Campo de trigo Jarad"
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-[#0d0d0d]" />
          </motion.div>

          <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-sans font-black mb-6 tracking-tighter text-white">
                <span className="block text-[#f6cf02] drop-shadow-sm">Jarad</span>
                <span className="font-bold">Pet Food</span>
              </h1>
              <p className="text-lg md:text-2xl max-w-2xl mx-auto font-medium leading-relaxed text-white/90 tracking-wide">
                <T keyName="about.hero.subtitle">Descubre nuestra historia y compromiso con el bienestar de tu mascota</T>
              </p>
            </motion.div>
          </div>

          <motion.div
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white/50 flex flex-col items-center gap-2 cursor-pointer"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          >
            <span className="text-xs uppercase tracking-[0.2em] font-medium">Descubre Más</span>
            <ChevronDown className="w-6 h-6" />
          </motion.div>
        </div>

        {/* Section 2: Stats Counter */}
        <div className="bg-[#0d0d0d] py-20 border-b border-[#f6cf02]/10 relative z-20">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
              {[
                { number: 30, suffix: '+', label: 'Años de Experiencia' },
                { number: 15, suffix: '+', label: 'Países' },
                { number: 500, suffix: '+', label: 'Referencias' },
                { number: 100, suffix: '%', label: 'Calidad Garantizada' },
              ].map((stat, idx) => (
                <div key={idx} className="text-center group">
                  <div className="text-4xl md:text-6xl font-black font-sans text-[#f6cf02] mb-2 group-hover:scale-110 transition-transform duration-500">
                    <CountUp to={stat.number} />{stat.suffix}
                  </div>
                  <p className="text-sm md:text-base text-gray-400 uppercase tracking-widest font-bold">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Purpose Intro */}
        <section className="py-32 bg-white text-center relative z-20">
          <div className="container mx-auto px-6">
            <motion.h2
              className="text-xl text-[#f6cf02] font-bold tracking-[0.2em] uppercase mb-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Nuestro Propósito
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-sans font-bold text-[#0d0d0d] max-w-3xl mx-auto leading-tight tracking-tight"
            >
              <T keyName="about.purpose.subtitle">En Jarad Pet Food creemos que una buena alimentación puede cambiar la vida de nuestras mascotas</T>
            </motion.p>
          </div>
        </section>

        {/* STACKING CARDS SECTION */}
        <div className="relative">

          {/* Card 1: SOMOS TIERRA */}
          <section className="relative min-h-screen py-20 lg:py-0 lg:sticky lg:top-0 lg:h-screen flex items-center bg-[#f9f6ef] overflow-hidden z-30">
            <div className="container mx-auto px-6">
              <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
                <div className="flex-1 space-y-8 order-2 lg:order-1">
                  <div className="w-16 h-1 bg-[#f6cf02]"></div>
                  <h3 className="text-4xl md:text-5xl lg:text-7xl font-sans font-black text-[#0d0d0d] tracking-tighter">
                    <T keyName="about.purpose.we_are_earth.title">SOMOS TIERRA</T>
                  </h3>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-medium max-w-xl">
                    <T keyName="about.purpose.we_are_earth.desc">
                      Nacimos en el corazón de Andalucía. Seleccionamos las mejores materias primas directamente del origen, garantizando frescura y calidad natural en cada grano.
                    </T>
                  </p>
                  <ul className="space-y-4 pt-4">
                    {['Ingredientes Locales', 'Cultivos Sostenibles', 'Selección Premium'].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-lg font-bold text-[#0d0d0d]">
                        <CheckCircle2 className="w-6 h-6 text-[#f6cf02]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex-1 relative order-1 lg:order-2 h-[300px] md:h-[400px] lg:h-auto w-full">
                  <div className="absolute -inset-4 bg-[#f6cf02] rounded-3xl opacity-20 -rotate-3 transform"></div>
                  <OptimizedImage
                    src="/images/about/field.webp"
                    alt="Campos de cultivo"
                    className="w-full h-full lg:h-[600px] object-cover rounded-3xl shadow-2xl relative z-10"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Card 2: SOMOS FAMILIA */}
          <section className="relative min-h-screen py-20 lg:py-0 lg:sticky lg:top-0 lg:h-screen flex items-center bg-white overflow-hidden z-40 border-t border-gray-100 shadow-[0_-10px_40px_rgba(0,0,0,0.1)]">
            <div className="container mx-auto px-6">
              <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
                <div className="flex-1 relative h-[300px] md:h-[400px] lg:h-auto w-full">
                  <div className="absolute -inset-4 bg-black rounded-3xl opacity-5 rotate-3 transform"></div>
                  <OptimizedImage
                    src="/images/perro-gato-jarad.webp"
                    alt="Familia Jarad"
                    className="w-full h-full lg:h-[600px] object-cover rounded-3xl shadow-2xl relative z-10"
                  />
                </div>
                <div className="flex-1 space-y-8">
                  <div className="w-16 h-1 bg-[#f6cf02]"></div>
                  <h3 className="text-4xl md:text-5xl lg:text-7xl font-sans font-black text-[#0d0d0d] tracking-tighter">
                    <T keyName="about.purpose.we_are_family.title">SOMOS FAMILIA</T>
                  </h3>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-medium max-w-xl">
                    <T keyName="about.purpose.we_are_family.desc">
                      Entendemos el vínculo único entre tú y tu mascota. Por eso, formulamos recetas que no solo alimentan, sino que cuidan y protegen a quienes más quieres.
                    </T>
                  </p>
                  <ul className="space-y-4 pt-4">
                    {['Nutrición Balanceada', 'Cuidado Integral', 'Amor en cada bocado'].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-lg font-bold text-[#0d0d0d]">
                        <CheckCircle2 className="w-6 h-6 text-[#f6cf02]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Card 3: SOMOS ALIMENTACIÓN */}
          <section className="relative min-h-screen py-20 lg:py-0 lg:sticky lg:top-0 lg:h-screen flex items-center bg-[#0d0d0d] text-white overflow-hidden z-50 shadow-[0_-10px_40px_rgba(0,0,0,0.2)]">
            <div className="container mx-auto px-6">
              <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
                <div className="flex-1 space-y-8 order-2 lg:order-1">
                  <div className="w-16 h-1 bg-[#f6cf02]"></div>
                  <h3 className="text-4xl md:text-5xl lg:text-7xl font-sans font-black text-white tracking-tighter">
                    <T keyName="about.purpose.we_are_nutrition.title">SOMOS ALIMENTACIÓN</T>
                  </h3>
                  <p className="text-lg md:text-xl text-gray-400 leading-relaxed font-medium max-w-xl">
                    <T keyName="about.purpose.we_are_nutrition.desc">
                      Combinamos tradición con innovación tecnológica. Nuestras instalaciones de vanguardia nos permiten crear alimentos de alta digestibilidad y palatabilidad superior.
                    </T>
                  </p>
                  <ul className="space-y-4 pt-4">
                    {['Tecnología Punta', 'Control de Calidad', 'Innovación Constante'].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-lg font-bold text-white">
                        <CheckCircle2 className="w-6 h-6 text-[#f6cf02]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex-1 relative order-1 lg:order-2 h-[300px] md:h-[400px] lg:h-auto w-full">
                  <div className="absolute -inset-4 bg-[#f6cf02] rounded-3xl opacity-20 rotate-2 transform"></div>
                  <OptimizedImage
                    src="/images/about/assembly.webp"
                    alt="Fábrica Jarad"
                    className="w-full h-full lg:h-[600px] object-cover rounded-3xl shadow-2xl relative z-10"
                  />
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* 4. HISTORY & TIMELINE */}
        <section id="historia" className="py-24 bg-white relative">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(#000_1px,transparent_1px)] [background-size:16px_16px]"></div>

          <div className="container mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-sans font-black text-[#0d0d0d] mb-6 tracking-tight">
                <T keyName="about.history.title">Nuestra Historia</T>
              </h2>
              <div className="w-24 h-1 bg-[#f6cf02] mx-auto rounded-full"></div>
            </div>

            {/* Timeline Items */}
            <div className="max-w-4xl mx-auto space-y-12">
              {[
                { year: '1980s', textKey: 'about.history.paragraph1', title: 'Los Orígenes' },
                { year: '1994', textKey: 'about.history.paragraph2', title: 'Nace Jarad' },
                { year: 'Hoy', textKey: 'about.history.paragraph3', title: 'Mirando al Futuro' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.2 }}
                  className="flex flex-col md:flex-row gap-8 items-start md:items-center relative"
                >
                  {/* Line connector (except last) */}
                  {idx !== 2 && (
                    <div className="absolute left-[39px] top-20 bottom-[-48px] w-[2px] bg-gray-200 md:hidden"></div>
                  )}

                  <div className="flex-shrink-0 w-20 h-20 rounded-full bg-[#f6cf02] flex items-center justify-center text-[#0d0d0d] font-bold text-xl shadow-lg z-10">
                    {item.year}
                  </div>
                  <div className="flex-1 bg-[#f9f6ef] p-8 rounded-2xl border border-[#f6cf02]/20 hover:shadow-xl transition-shadow duration-300">
                    <h4 className="text-xl font-bold mb-3 font-sans uppercase tracking-wide">{item.title}</h4>
                    <p className="text-gray-600 leading-relaxed text-lg">
                      <T keyName={item.textKey}>
                        Contenido histórico...
                      </T>
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Video Embed */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="mt-24 max-w-5xl mx-auto"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-video border-[8px] border-white bg-black">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/bBxY-503eKc?autoplay=0&rel=0"
                  title="Jarad Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 5. GLOBAL PRESENCE */}
        <section className="bg-[#0d0d0d] text-white py-24 overflow-hidden relative">
          {/* Decorative glow */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#f6cf02] rounded-full filter blur-[150px] opacity-10 pointer-events-none"></div>

          <div className="container mx-auto px-6">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <div className="flex-1 space-y-8">
                <h2 className="text-4xl md:text-6xl font-sans font-black leading-tight tracking-tight">
                  Presentes en más de <br /><span className="text-[#f6cf02]">15 países</span>
                </h2>
                <p className="text-xl text-gray-400 font-light leading-relaxed max-w-xl">
                  <T keyName="about.history.expansion.description">
                    Seguimos creciendo con paso firme, llevando nuestra filosofía de alimentación responsable a nuevos hogares y mercados.
                  </T>
                </p>

                <div className="flex flex-wrap gap-3">
                  {[
                    { name: 'Europa', key: 'about.history.expansion.regions.europe' },
                    { name: 'Medio Oeste', key: 'about.history.expansion.regions.middle_east' },
                    { name: 'Asia Central', key: 'about.history.expansion.regions.central_asia' },
                    { name: 'África', key: 'about.history.expansion.regions.africa' },
                    { name: 'América del Sur', key: 'about.history.expansion.regions.south_america' }
                  ].map((region, i) => (
                    <span key={i} className="px-4 py-2 border border-[#f6cf02]/30 rounded-full text-[#f6cf02] hover:bg-[#f6cf02] hover:text-black transition-all duration-300">
                      <T keyName={region.key}>{region.name}</T>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex-1 flex justify-center lg:justify-end w-full">
                <div className="w-full max-w-[500px] aspect-square relative grayscale hover:grayscale-0 transition-all duration-700">
                  <Globe className="scale-125" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 6. CERTIFICATIONS - TRUST BAR */}
        <section id="certificados" className="py-20 bg-white">
          <div className="container mx-auto px-6 text-center">
            <h3 className="text-2xl font-bold uppercase tracking-widest mb-12 text-gray-400">
              <T keyName="about.certificates.title">Nuestras Certificaciones</T>
            </h3>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-wrap justify-center items-center gap-12 md:gap-24 opacity-80 hover:opacity-100 transition-opacity"
            >
              <img src="/images/Certifications.png" alt="Certificados" className="h-24 md:h-32 object-contain filter grayscale hover:grayscale-0 transition-all duration-500" />
            </motion.div>
            <p className="text-gray-500 max-w-2xl mx-auto mt-12 italic">
              "<T keyName="about.certificates.subtitle">Nos comprometemos a ofrecer productos de la más alta calidad</T>"
            </p>
          </div>
        </section>

        {/* 7. CTA */}
        <section className="py-24 bg-[#f6cf02] relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('/images/pattern-paw.png')] opacity-10"></div>
          <div className="container mx-auto px-6 text-center relative z-10 text-black">
            <h2 className="text-4xl md:text-5xl font-sans font-black mb-6 tracking-tight">
              <T keyName="about.cta.title">¿Quieres lo mejor para tu mascota?</T>
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto font-medium opacity-80">
              <T keyName="about.cta.subtitle">Explora nuestra gama completa y encuentra la nutrición perfecta.</T>
            </p>
            <Link
              to="/catalog"
              className="inline-flex items-center gap-2 bg-black text-[#f6cf02] px-10 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 transform"
            >
              <T keyName="about.cta.button">Ver Catálogo Completo</T>
              <ChevronDown className="-rotate-90" />
            </Link>
          </div>
        </section>

      </div>
    </>
  );
};

export default Nosotros;